/**
 * src/utils/governanceRbacTasks.js
 *
 * Governance RBAC (FDNA-01) — the two standing conditions that block or
 * weaken the risk lifecycle's accountability gates, turned into work.
 *
 *   1. A REQUIRED governance role nobody holds. risks-write refuses a gated
 *      transition with a 409 naming the role when no one can perform it; a
 *      banner in Settings tells an administrator who happens to look, a task
 *      tells the one who owns the work (§12: a rule that governs a human is
 *      not a mechanism).
 *   2. An OPT-OUT that is due for review, or a legacy opt-out with no
 *      recorded reason. Switching governance off is an acceptance of risk for
 *      the whole tenant; §11 — "if accepting a risk generated no work, the
 *      organisation has not accepted it — it has ignored it."
 *
 * Same shape and the same two rules as identityAccessTasks.js, in the same
 * ONE task system (state.adhocTasks[], CLAUDE.md "One Task System"):
 *
 *  - IDEMPOTENT. One open task per tenant per condition, keyed by
 *    `governanceRbacKey`; the hourly sweep never creates a second.
 *  - WITHDRAWN ONLY ON EVIDENCE. A task closes when its condition was
 *    observed to be gone. An unheld-role task is never closed on the strength
 *    of a governance read that failed (IDN-B-004).
 *
 * The key field is deliberately NOT `identityKey`: that field mounts the
 * identity access proposal panel, which proposes per-person grants. These
 * tasks are tenant-level and are actioned on Settings → Governance Roles.
 */

import { generateId, now } from './formatting.js';
import { closeTask, systemClosure } from './taskClosure.js';
import { GOVERNANCE_STATUS } from './roleMatrix.js';
import { governanceRbacStanding, unheldRequiredRoles } from './governanceRbacFlag.js';

export const GOVERNANCE_RBAC_TASK_SOURCE = 'governance-rbac';

export const GOVERNANCE_ROLE_LABELS = Object.freeze({
  RISK_OWNER: 'Risk Owner',
  REVIEWER: 'Reviewer',
  APPROVER: 'Approver',
});

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function unheldRoleKey(roleKey) {
  return `${GOVERNANCE_RBAC_TASK_SOURCE}|unheld-role|${roleKey}`;
}
export const OPT_OUT_REVIEW_KEY = `${GOVERNANCE_RBAC_TASK_SOURCE}|opt-out-review`;

function isClosed(task) {
  return task.status === 'closed' || task.status === 'completed';
}

/**
 * @param {object} input
 * @param {object} input.features          state.features
 * @param {string} input.governanceStatus  GOVERNANCE_STATUS of this sweep's read
 * @param {Array}  input.assignments       active governance role assignments
 * @param {Array}  input.users             tenant users (for "live holder")
 * @param {Array}  input.adhocTasks
 * @param {string} input.assigneeId
 * @param {number} [input.nowMs]
 * @returns {{ toCreate: Array, toClose: Array<{id:string,key:string,reason:string}> }}
 */
export function planGovernanceRbacTasks({
  features,
  governanceStatus,
  assignments = [],
  users = [],
  adhocTasks = [],
  assigneeId = null,
  nowMs = Date.now(),
}) {
  const standing = governanceRbacStanding(features, nowMs);
  const trusted = governanceStatus === GOVERNANCE_STATUS.OK;

  const current = new Map(); // key -> spec
  if (standing === 'on' && trusted) {
    for (const role of unheldRequiredRoles(assignments, users)) {
      current.set(unheldRoleKey(role), { kind: 'unheld-role', role });
    }
  }
  if (standing === 'legacy' || standing === 'review-due') {
    current.set(OPT_OUT_REVIEW_KEY, { kind: 'opt-out-review', standing });
  }

  const ours = (adhocTasks || []).filter(
    (t) => t?.sourceJobId === GOVERNANCE_RBAC_TASK_SOURCE && t.governanceRbacKey && !isClosed(t)
  );
  const openKeys = new Set(ours.map((t) => t.governanceRbacKey));

  const toCreate = [];
  for (const [key, spec] of current) {
    if (!openKeys.has(key)) toCreate.push(buildTask(key, spec, assigneeId, nowMs, features));
  }

  const toClose = [];
  for (const t of ours) {
    if (current.has(t.governanceRbacKey)) continue;
    if (t.governanceRbacKind === 'unheld-role') {
      // Observable only when the store was read — or when the tenant is opted
      // out, in which case no transition is gated on the role any more.
      if (standing === 'on' && !trusted) continue;
      toClose.push({
        id: t.id,
        key: t.governanceRbacKey,
        reason:
          standing === 'on'
            ? `Resolved — the ${GOVERNANCE_ROLE_LABELS[t.governanceRoleKey] || t.governanceRoleKey} role is now held.`
            : 'Withdrawn — governance RBAC is opted out, so no transition is gated on this role.',
      });
    } else {
      toClose.push({
        id: t.id,
        key: t.governanceRbacKey,
        reason:
          standing === 'on'
            ? 'Resolved — governance RBAC was re-enabled.'
            : 'Resolved — a reason and a new review date were recorded for the opt-out.',
      });
    }
  }

  return { toCreate, toClose };
}

function buildTask(key, spec, assigneeId, nowMs, features) {
  const base = {
    id: 'task_' + generateId(),
    type: 'automatic',
    status: 'open',
    assigneeId,
    createdAt: now(),
    evidence: [],
    controls: [],
    engineGenerated: true,
    sourceJobId: GOVERNANCE_RBAC_TASK_SOURCE,
    governanceRbacKey: key,
    governanceRbacKind: spec.kind,
  };
  if (spec.kind === 'unheld-role') {
    const label = GOVERNANCE_ROLE_LABELS[spec.role] || spec.role;
    return {
      ...base,
      title: `Assign the ${label} governance role`,
      description: [
        `Nobody in this tenant holds the ${label} governance role.`,
        `A risk with no ${label.toLowerCase()} assigned on the risk itself cannot move through the step that role gates — the server refuses it and names the missing role.`,
        'Assign it on Settings → Governance Roles (/settings?tab=governance-roles). This task closes itself when the role is held.',
      ].join(' '),
      taskType: 'approval',
      priority: 'high',
      dueDate: new Date(nowMs + 7 * MS_PER_DAY).toISOString(),
      governanceRoleKey: spec.role,
    };
  }
  const rec = features?.governanceRbacV1OptOut;
  const lead =
    spec.standing === 'legacy'
      ? 'Governance RBAC is switched off for this tenant and no reason or review date was ever recorded for it. Anyone signed in can approve risk decisions.'
      : `The governance RBAC opt-out was due for review on ${String(rec?.reviewBy || '').slice(0, 10)}. Recorded reason: "${rec?.reason || ''}".`;
  return {
    ...base,
    title: 'Review the governance RBAC opt-out',
    description: [
      lead,
      'Either re-enable governance RBAC, or record why it stays off and when it will next be reviewed, on Settings → Risk Management. This task closes itself when one of those is done.',
    ].join(' '),
    taskType: 'review',
    priority: 'high',
    dueDate: new Date(nowMs + 14 * MS_PER_DAY).toISOString(),
  };
}

/** Apply a plan to a state draft. */
export function applyGovernanceRbacTasks(draft, plan) {
  if (!plan) return;
  const closing = new Map(plan.toClose.map((c) => [c.id, c.reason]));
  if (closing.size) {
    draft.adhocTasks = (draft.adhocTasks || []).map((t) =>
      closing.has(t.id)
        ? // A system closure record (GH #7704): why, and the sweep that closed it.
          closeTask(
            t,
            systemClosure({
              reason: closing.get(t.id),
              trigger: 'governance-rbac-sweep',
              at: now(),
            })
          )
        : t
    );
  }
  if (plan.toCreate.length) {
    draft.adhocTasks = [...(draft.adhocTasks || []), ...plan.toCreate];
  }
}
